import { useState } from 'react';
import { X, Search, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePets } from '../../context/PetsContext';

const RELATIONSHIP_LABEL_KEYS: Record<string, string> = {
  Sibling: 'family.relationshipLabel.Sibling',
  Mother: 'family.relationshipLabel.Mother',
  Father: 'family.relationshipLabel.Father',
  'Current Pet': 'family.relationshipLabel.Current Pet',
  Offspring: 'family.relationshipLabel.Offspring',
  'Maternal Grandmother': 'family.relationshipLabel.Maternal Grandmother',
  'Maternal Grandfather': 'family.relationshipLabel.Maternal Grandfather',
  'Paternal Grandmother': 'family.relationshipLabel.Paternal Grandmother',
  'Paternal Grandfather': 'family.relationshipLabel.Paternal Grandfather',
  'Grandmother (Maternal)': 'family.relationshipLabel.Grandmother (Maternal)',
  'Grandfather (Maternal)': 'family.relationshipLabel.Grandfather (Maternal)',
  'Grandmother (Paternal)': 'family.relationshipLabel.Grandmother (Paternal)',
  'Grandfather (Paternal)': 'family.relationshipLabel.Grandfather (Paternal)',
};

interface SearchFamilyMemberModalProps {
  onClose: () => void;
  onBack: () => void;
  onSelect: (member: any) => void;
  relationship: string;
}

interface SearchResult {
  id: string;
  did: string;
  name: string;
  species: string;
  breed: string;
  gender: string;
  imageUrl?: string;
  microchipId?: string;
  ownerClerkId?: string;
}

export function SearchFamilyMemberModal({ onClose, onBack, onSelect, relationship }: SearchFamilyMemberModalProps) {
  const { t } = useTranslation();
  const { searchPets } = usePets();
  const [searchQuery, setSearchQuery] = useState('');
  const [microchipId, setMicrochipId] = useState('');
  const [breederRef, setBreederRef] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const relationshipDisplay = (RELATIONSHIP_LABEL_KEYS[relationship] && t(RELATIONSHIP_LABEL_KEYS[relationship])) || relationship;

  const handleSearch = async () => {
    if (!searchQuery.trim() && !microchipId.trim() && !breederRef.trim()) {
      setError(t('family.searchModal.errorCriteria'));
      return;
    }

    setIsLoading(true);
    setError(null);
    setHasSearched(true);
    
    try {
      const response = await searchPets({
        nameOrBreed: searchQuery.trim() || undefined,
        microchipId: microchipId.trim() || undefined,
        breederReference: breederRef.trim() || undefined,
      });
      setSearchResults(response.pets as any);
    } catch (err) {
      console.error('Search failed:', err);
      setError(t('family.searchModal.errorFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl border dark:border-white/8 max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl mb-1" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}>{t('family.searchModal.title')}</h2>
              <p className="text-sm opacity-90" style={{ fontFamily: 'Inter, sans-serif' }}>{t('family.addMemberModal.adding', { relationship: relationshipDisplay })}</p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-auto p-6">
          {/* Search Form */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm text-gray-700 dark:text-white/90 mb-2" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
                {t('family.searchModal.petNameOrBreed')}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('family.searchModal.petNameOrBreedPlaceholder')}
                  className="w-full px-4 py-2 pr-10 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-white/40" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700 dark:text-white/90 mb-2" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
                  {t('family.searchModal.microchipIdOptional')}
                </label>
                <input
                  type="text"
                  value={microchipId}
                  onChange={(e) => setMicrochipId(e.target.value)}
                  placeholder="982000123456789"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>

              <div>
                <label className="block text-sm text-gray-700 dark:text-white/90 mb-2" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
                  {t('family.searchModal.breederRefOptional')}
                </label>
                <input
                  type="text"
                  value={breederRef}
                  onChange={(e) => setBreederRef(e.target.value)}
                  placeholder={t('family.searchModal.breederRefPlaceholder')}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#0F0E16] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                />
              </div>
            </div>

            <button
              onClick={handleSearch}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 text-white px-6 py-3 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
            >
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
              {isLoading ? t('family.searchModal.searching') : t('family.searchModal.searchButton')}
            </button>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-500/30 rounded-lg flex items-center gap-3 text-red-700 dark:text-red-300 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Search Results */}
          {hasSearched && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>{t('family.searchModal.searchResults')}</h3>
                <span className="text-sm text-gray-500 dark:text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>{t('family.searchModal.found', { count: searchResults.length })}</span>
              </div>

              {searchResults.length > 0 ? (
                <div className="space-y-3">
                  {searchResults.map((result) => (
                    <div key={result.id} className="bg-white dark:bg-[#0F0E16] border-2 border-gray-200 dark:border-white/20 rounded-xl p-4 hover:border-blue-500 dark:hover:border-blue-400 transition-colors">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center text-2xl">
                            {result.species === 'dog' ? '🐕' : '🐈'}
                          </div>
                          <div>
                            <h4 className="mb-1 text-gray-900 dark:text-white" style={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>{result.name}</h4>
                            <div className="text-sm text-gray-600 dark:text-white/70" style={{ fontFamily: 'Inter, sans-serif' }}>{result.breed}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-2 py-1 rounded">
                          <CheckCircle className="w-3 h-3" />
                          {t('family.verifiedDid')}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                        <div>
                          <span className="text-gray-500 dark:text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>{t('family.searchModal.gender')}:</span>
                          <span className="ml-2 text-gray-900 dark:text-white capitalize" style={{ fontFamily: 'Inter, sans-serif' }}>{result.gender}</span>
                        </div>
                        {result.ownerClerkId && (
                          <div>
                            <span className="text-gray-500 dark:text-white/50" style={{ fontFamily: 'Inter, sans-serif' }}>{t('family.searchModal.ownerId')}:</span>
                            <span className="ml-2 text-gray-900 dark:text-white truncate block" title={result.ownerClerkId} style={{ fontFamily: 'Inter, sans-serif' }}>{result.ownerClerkId.substring(0, 8)}...</span>
                          </div>
                        )}
                      </div>

                      {result.microchipId && (
                        <div className="text-xs text-gray-500 dark:text-white/50 mb-3" style={{ fontFamily: 'Inter, sans-serif' }}>
                          {t('family.searchModal.microchip')}: {result.microchipId}
                        </div>
                      )}

                      <div className="bg-gray-50 dark:bg-[#16151D] border dark:border-white/20 rounded-lg p-2 mb-3">
                        <div className="text-xs text-gray-500 dark:text-white/50 mb-1" style={{ fontFamily: 'Inter, sans-serif' }}>DID</div>
                        <div className="font-mono text-xs break-all text-gray-700 dark:text-white/90">{result.did}</div>
                      </div>

                      <button
                        onClick={() => onSelect(result)}
                        className="w-full bg-blue-600 dark:bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 transition-colors"
                        style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
                      >
                        {t('family.searchModal.selectAs', { relationship: relationshipDisplay })}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-[#0F0E16] border border-gray-200 dark:border-white/20 rounded-xl p-8 text-center">
                  <AlertCircle className="w-12 h-12 text-gray-400 dark:text-white/40 mx-auto mb-3" />
                  <h4 className="text-gray-700 dark:text-white/90 mb-2" style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}>{t('family.searchModal.noMatchesFound')}</h4>
                  <p className="text-sm text-gray-500 dark:text-white/70 mb-4" style={{ fontFamily: 'Inter, sans-serif' }}>
                    {t('family.searchModal.noMatchesDesc')}
                  </p>
                  <button
                    onClick={onBack}
                    className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm"
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {t('family.searchModal.tryAddingManually')}
                  </button>
                </div>
              )}
            </div>
          )}

          {!hasSearched && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-500/30 rounded-lg p-4">
              <div className="text-sm text-blue-900 dark:text-blue-100" style={{ fontFamily: 'Inter, sans-serif' }}>
                <div className="mb-1" style={{ fontWeight: 600 }}>🔍 {t('family.searchModal.searchTips')}</div>
                <ul className="text-xs text-blue-700 dark:text-blue-300 space-y-1 ml-4">
                  <li>• {t('family.searchModal.tip1')}</li>
                  <li>• {t('family.searchModal.tip2')}</li>
                  <li>• {t('family.searchModal.tip3')}</li>
                  <li>• {t('family.searchModal.tip4')}</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer Buttons */}
        <div className="p-6 bg-gray-50 dark:bg-[#0F0E16] border-t border-gray-200 dark:border-white/20 flex gap-3">
          <button
            onClick={onBack}
            className="flex-1 px-6 py-3 border border-gray-300 dark:border-white/20 bg-white dark:bg-[#16151D] text-gray-900 dark:text-white rounded-lg hover:bg-white dark:hover:bg-white/5 transition-colors"
            style={{ fontFamily: 'Sora, sans-serif', fontWeight: 600 }}
          >
            {t('family.searchModal.back')}
          </button>
        </div>
      </div>
    </div>
  );
}